import React, { useState } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import "./styles.css";
import { initTelegramWebApp, syncWebAppChrome } from "./tg/webapp";
import { initTheme } from "./store/theme";
import { initLocale } from "./i18n";
import { captureAccessTokenFromUrl } from "./api/client";
import ResetPasswordScreen from "./features/auth/ResetPasswordScreen";
import { clearResetTokenFromUrl, resetTokenFromUrl } from "./features/auth/resetUrl";
import VerifyEmailScreen from "./features/auth/VerifyEmailScreen";
import BrandApplier from "./features/brand/BrandApplier";
import { clearVerifyTokenFromUrl, verifyTokenFromUrl } from "./features/auth/verifyUrl";

// Прямая ссылка из `/app`: забираем токен в localStorage и чистим адресную
// строку ДО первых запросов — иначе первый же `GET /api/me` ушёл бы без него.
captureAccessTokenFromUrl();
initTelegramWebApp();
// GHG11(11): тему наносим до первого рендера — иначе видно вспышку чужой темы.
syncWebAppChrome(initTheme());
// Язык — до первого рендера: иначе английский интерфейс на миг показывал бы
// русские подписи, а `<html lang>` оставался бы пустым.
initLocale();

const qc = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

/**
 * Развилка до приложения.
 *
 * Ссылка из письма (`/reset?token=…`) открывает свой экран и не поднимает
 * `App`: сессии у человека в этот момент может не быть вовсе, и `GET /api/me`
 * только зря сходил бы в 401. После смены пароля адрес чистится — ссылка
 * одноразовая, оставаться в истории браузера ей незачем.
 */
function Root() {
  const [resetToken, setResetToken] = useState(() => resetTokenFromUrl());
  const [verifyToken, setVerifyToken] = useState(() => verifyTokenFromUrl());

  if (resetToken) {
    return (
      <ResetPasswordScreen
        token={resetToken}
        onDone={() => {
          clearResetTokenFromUrl();
          setResetToken(null);
        }}
      />
    );
  }

  // Подтверждение почты: ссылку открывают и без входа, поэтому отдельный
  // маршрут до `App` — как у сброса пароля.
  if (verifyToken) {
    return (
      <VerifyEmailScreen
        token={verifyToken}
        onDone={() => {
          clearVerifyTokenFromUrl();
          setVerifyToken(null);
        }}
      />
    );
  }

  return <App />;
}

// Заставка из `index.html` отрисована ещё до бандла: убираем её сами, чтобы
// React не рисовал приложение вокруг чужого узла (и чтобы на медленном канале
// между каркасом и первым кадром не было сразу двух скелетонов).
document.getElementById("boot-splash")?.remove();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      {/* Бренд клиента: заголовок вкладки и акцент — до первого экрана. */}
      <BrandApplier />
      <Root />
    </QueryClientProvider>
  </React.StrictMode>,
);

/**
 * PWA (B2B_PLATFORM_PLAN, шаг 2.3): оболочка для установки и офлайн-старта.
 *
 * Только в сборке: в dev service worker кешировал бы модули Vite и правки
 * перестали бы доезжать до браузера. Ошибка регистрации ничего не ломает —
 * приложение просто работает как обычный сайт, поэтому `catch` молчит.
 */
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}
